import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from 'react';

import { createRecordByKey } from '@shared/helpers/array.helpers';
import { getConversionFactorValue } from '@shared/helpers/conversion-factors.helpers';
import {
  getId,
  mapFilterRecord,
  mapObject,
} from '@shared/helpers/object.helpers';
import { buildElementMaxResultsRecord } from '@shared/helpers/proposal-results-record.helpers';
import {
  buildProposalEffectiveColorIndexMap,
  getProposalSwatchColor,
} from '@shared/helpers/proposal.helpers';
import {
  flattenElements,
  isElement,
} from '@shared/helpers/recursive-element.helpers';
import { getResultsRecord } from '@shared/helpers/results.helpers';
import {
  Baseline,
  OneOfElementListElements,
} from '@shared/models/project.model';
import { IProposal } from '@shared/models/proposals.model';
import { ArrayOrRecord, ItemOrItemId } from '@shared/models/type-helpers.model';
import {
  ConversionFactorGroupKey,
  Results,
  ResultsRecord,
} from '@shared/models/unit.model';
import { createLocalStorageRecordStore } from '@client/helpers/local-storage.helpers';
import { useObjectMemo } from '@client/hooks/use-object.hooks';
import {
  getCo2eChartDataFromResults,
  getCostChartDataFromResults,
} from '@ui/BarChart/helpers/bar-chart.helpers';
import { useResolvedProjectTarget } from '@features/scope/scope-targets.hooks';
import { useSelectedVersion, useSelectedVersionId } from '@client/store/ui';

import {
  useGetResultsPerGFA,
  useResultsPerGFA,
} from '../results/use-results.hooks';
import { useSelectedLifecycles } from '@features/lifecycles/LifecyclesSelect';
import { useEffectiveSbefGroups } from '@features/sbef/use-effective-sbef-groups.hook';
import { filterResultsByScope } from '@shared/helpers/results-scope.helpers';
import { useConservativeGenericCarbon } from '@features/results/conservative-generic-carbon.hook';
import { useSharedMemo } from '@client/hooks/use-utils.hooks';
import { useDisplayCurrency } from '@features/currency/selected-display-currency.hook';
import { getDisplayValueData } from '@shared/helpers/display/get-display-value-data.helper';
import { useProposals } from './hooks/use-proposals.hooks';
import { useSelectedProposalId } from '@client/hooks/use-selected-proposal-id.hooks';
import {
  computeProposalOverviewCo2PerGfaScaleMax,
  computeProposalOverviewCostPerGfaScaleMax,
} from '@shared/helpers/proposals/proposals-overview-scale.helpers';
import {
  getProposal,
  getProposals,
} from '@shared/helpers/proposals/proposals.helpers';

export const {
  setItem: setHiddenProposal,
  useStoreItem: useHiddenProposal,
  useStoreRecord: useHiddenProposalsRecord,
} = createLocalStorageRecordStore<IProposal['id'], boolean>('hidden_proposals');

export {
  useAddProposal,
  useDeleteProposal,
  useElementSelectedProposalColor,
  useItemsSelectedProposalColor,
  useProposal,
  useProposals,
  useProposalsUsingElement,
  useRenameProposal,
  useReorderProposals,
  useSelectedProposal,
  useUpdateProposals,
} from './hooks/use-proposals.hooks';

export const useGetProposalColor = () => {
  const version = useSelectedVersion();
  const proposals = useProposals();
  return useCallback(
    (proposalOrId: ItemOrItemId<IProposal>) => {
      if (proposalOrId === undefined) return '#CCC';
      const id = getId(proposalOrId);
      const row = getProposal(version, id);
      if (!row) return '#CCC';
      const effective = buildProposalEffectiveColorIndexMap(proposals);
      return getProposalSwatchColor(effective.get(id) ?? row.colorIndex);
    },
    [version, proposals],
  );
};

export const useProposalColor = (proposalOrId: ItemOrItemId<IProposal>) => {
  const getProposalColor = useGetProposalColor();
  return useMemo(
    () => getProposalColor(proposalOrId),
    [getProposalColor, proposalOrId],
  );
};

/**
 * Get results record for a specific proposal. If no proposal is passed, the selected proposal is used.
 * @param proposalOrId
 * @param applyLifecycleFilter - If true (default), the results record will be filtered by the selected lifecycles
 * @returns
 */
export const useProposalResultsRecord = (
  proposalOrId?: ItemOrItemId<IProposal> | undefined,
  {
    applyLifecycleFilter = true,
    applySbefFilter = true,
    version,
  }: {
    applyLifecycleFilter?: boolean;
    /**
     * When true (default), collapse each row to the selected SBEF groups. The product
     * list must pass `false`: its rows are flat per-product totals with no SBEF breakdown,
     * so a strict-subset collapse zeroes them (DEV-3492). SBEF scoping is instead applied
     * as row inclusion in {@link createProductListGroups}.
     */
    applySbefFilter?: boolean;
    version?: Baseline;
  } = {},
): ResultsRecord => {
  const selectedVersion = useSelectedVersion();
  const resolvedVersion = version ?? selectedVersion;

  const selectedProposalId = useSelectedProposalId();

  // Resolve the effective proposal id: only use persisted proposal rows on this
  // version — never substitute ids from another version or stale router state.
  const id = useMemo(() => {
    if (!resolvedVersion) return undefined;
    const explicitId = proposalOrId ? getId(proposalOrId) : undefined;
    return explicitId ?? selectedProposalId;
  }, [resolvedVersion, proposalOrId, selectedProposalId]);

  const proposalRecord = useMemo(() => {
    if (!resolvedVersion) return undefined;
    return getResultsRecord(resolvedVersion, id ?? undefined);
  }, [resolvedVersion, id]);

  const selectedLifecycles = useSelectedLifecycles();
  const effectiveSbefGroups = useEffectiveSbefGroups();
  const conservative = useConservativeGenericCarbon();

  // Use a process-wide shared memo so every consumer (ElementsView, product
  // list hooks, sorting, element groups, etc.) reads the same object reference
  // for identical inputs. Relying on a per-component `useMemo` here forced each
  // caller's `useCallback`-wrapped lifecycle filter into the dep list, so each
  // component produced its own object identity for the same data and busted
  // downstream `useSharedMemo` caches on every render.
  return useSharedMemo(
    () => {
      const base = proposalRecord ?? {};
      if (!applyLifecycleFilter) return base;
      return mapObject(base, (results) =>
        filterResultsByScope(results, {
          lifecycles: selectedLifecycles,
          sbefGroups: applySbefFilter ? effectiveSbefGroups : [],
          conservative,
        }),
      );
    },
    `useProposalResultsRecord:${id ?? 'none'}:${
      applyLifecycleFilter ? 'filtered' : 'raw'
    }:${applySbefFilter ? effectiveSbefGroups.join(',') : 'nosbef'}:${conservative}`,
    [
      proposalRecord,
      selectedLifecycles,
      effectiveSbefGroups,
      conservative,
      applyLifecycleFilter,
      applySbefFilter,
    ],
  );
};

/**
 * Get the results of all values for a specific proposal.
 * I.E. How much would a version emit/cost for a specific proposal
 * @param proposalOrId
 * @returns
 */
export const useProposalResults = (
  proposalOrId?: ItemOrItemId<IProposal>,
): Results => {
  const id = useSelectedVersionId();
  const record = useProposalResultsRecord(proposalOrId);

  return useMemo(() => {
    if (!id || !record[id]) {
      return {};
    }
    // Each proposal record stores the total results for the proposal as record[version.id] since version is the top-level element
    return record[id];
  }, [id, record]);
};

/**
 * Get all results records (grouped by proposals) in the selected version (baseline row optional).
 * @param applyLifecycleFilter - If true (default), the results record will be filtered by the selected lifecycles
 * @param includeBaseline - If true (default), the baseline (version-level) record will be included
 * @returns
 */
export const useProposalsResultRecords = (
  applyLifecycleFilter: boolean = true,
  includeBaseline: boolean = true,
  versionOverride?: Baseline,
): Record<IProposal['id'], ResultsRecord> => {
  const selectedVersion = useSelectedVersion();
  const selectedProposals = useProposals();
  const version = versionOverride ?? selectedVersion;
  const proposals = useMemo(
    () => (versionOverride ? getProposals(versionOverride) : selectedProposals),
    [selectedProposals, versionOverride],
  );
  const selectedLifecycles = useSelectedLifecycles();
  const effectiveSbefGroups = useEffectiveSbefGroups();
  const conservative = useConservativeGenericCarbon();

  // Shared across consumers for reference stability. A per-component `useMemo`
  // here (with `useFilterResultsRecordBySelectedLifecycles` as a dep) emitted a
  // new record object on every render, which rippled into `useElementSortFn` /
  // `useElementMaxResultsRecord` and caused the sort-filter store to be
  // re-written in a post-commit effect — producing a second (and third) render
  // cascade on every tab toggle.
  return useSharedMemo(
    () => {
      if (!version) return {};
      const records = createRecordByKey(
        proposals as ArrayOrRecord<IProposal>,
        'id',
        (proposal) => {
          const record = getResultsRecord(version, proposal);
          if (!applyLifecycleFilter) return record;
          return mapObject(record, (results) =>
            filterResultsByScope(results, {
              lifecycles: selectedLifecycles,
              sbefGroups: effectiveSbefGroups,
              conservative,
            }),
          );
        },
      );

      // Optionally include the baseline (version-level) record under the
      // version id so callers can treat baseline + proposals uniformly.
      const baselineRecord = version.resultsRecord;
      if (includeBaseline && baselineRecord) {
        records[version.id] = applyLifecycleFilter
          ? mapObject(baselineRecord, (results) =>
              filterResultsByScope(results, {
                lifecycles: selectedLifecycles,
                sbefGroups: effectiveSbefGroups,
                conservative,
              }),
            )
          : baselineRecord;
      }

      return records;
    },
    `useProposalsResultRecords:${version?.id ?? 'none'}:${
      applyLifecycleFilter ? 'filtered' : 'raw'
    }:${includeBaseline ? 'with-baseline' : 'no-baseline'}:${effectiveSbefGroups.join(',')}:${conservative}`,
    [
      proposals,
      version,
      selectedLifecycles,
      effectiveSbefGroups,
      conservative,
      applyLifecycleFilter,
      includeBaseline,
    ],
  );
};

/**
 * Get results for all proposals of a specific element
 * @param elementOrId
 * @returns
 */
export const useElementProposalsResults = (
  elementOrId: ItemOrItemId<OneOfElementListElements>,
) => {
  const id = getId(elementOrId);
  const records = useProposalsResultRecords();
  return useMemo(() => mapFilterRecord(records, (r) => r[id]), [id, records]);
};

/**
 * Per-element {@link Results} after picking the winning proposal for {@link byProperty}
 * (see {@link buildElementMaxResultsRecord}). Use for list sorting instead of calling
 * {@link getElementMaxResultsFromProposals} per compare.
 */
export const useElementMaxResultsRecord = (
  byProperty: keyof Results | ConversionFactorGroupKey = 'co2e',
): ResultsRecord => {
  const records = useProposalsResultRecords();
  return useMemo(
    () => buildElementMaxResultsRecord(records, byProperty),
    [records, byProperty],
  );
};

export type ElementListMaxValues = {
  /** Max CO2e per GFA across all (element × proposal) in the version. */
  maxCo2: number;
  /**
   * Max cost per GFA across all (element × proposal) in the version, already
   * run through the display-currency formatter used by list rows.
   */
  maxCost: number;
};

const EMPTY_ELEMENT_LIST_MAX_VALUES: ElementListMaxValues = {
  maxCo2: 0,
  maxCost: 0,
};

/**
 * Pure calculation behind {@link useElementListMaxValues}. Extracted so the
 * context provider can memoize a single result and every row can read it
 * from context instead of re-running this N-row times.
 *
 * Hoists the GFA division and display-currency conversion out of the
 * O(proposals × elements) inner loop: since both operations are monotonic
 * linear transforms, `max(f(x))` equals `f(max(x))` and we only pay for
 * them once at the end.
 */
const computeElementListMaxValues = (
  version: Baseline,
  records: Record<IProposal['id'], ResultsRecord>,
  getResultsPerGFA: ReturnType<typeof useGetResultsPerGFA>,
  displayCurrency: ReturnType<typeof useDisplayCurrency>,
): ElementListMaxValues => {
  const allElements = flattenElements(version).filter(isElement);
  let maxRawCo2 = 0;
  let maxRawSek = 0;
  for (const record of Object.values(records)) {
    for (const el of allElements) {
      const raw = record[el.id];
      if (!raw) continue;
      const co2 = getConversionFactorValue(raw, 'co2e') ?? 0;
      if (co2 > maxRawCo2) maxRawCo2 = co2;
      const sek = getConversionFactorValue(raw, 'sek');
      if (sek != null && sek > maxRawSek) maxRawSek = sek;
    }
  }

  const maxCo2 = getResultsPerGFA(maxRawCo2);
  const maxSekPerGFA = getResultsPerGFA(maxRawSek);
  const maxCost =
    displayCurrency.isEnabled && maxSekPerGFA > 0
      ? getDisplayValueData(
          { value: maxSekPerGFA, unit: 'sek' },
          {
            outputUnit: { unit: displayCurrency.current },
            notation: 'compact',
          },
        ).valueAsNumber
      : 0;

  return { maxCo2, maxCost };
};

const ElementListMaxValuesContext = createContext<ElementListMaxValues | null>(
  null,
);

/**
 * Compute {@link ElementListMaxValues} once for the subtree. Wrap the element
 * list at its root so every row reads from context instead of independently
 * running {@link computeElementListMaxValues} (O(proposals × elements) per row).
 */
export const ElementListMaxValuesProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const version = useSelectedVersion();
  const records = useProposalsResultRecords();
  const getResultsPerGFA = useGetResultsPerGFA();
  const displayCurrency = useDisplayCurrency();

  const value = useMemo(
    () =>
      version
        ? computeElementListMaxValues(
            version,
            records,
            getResultsPerGFA,
            displayCurrency,
          )
        : EMPTY_ELEMENT_LIST_MAX_VALUES,
    [version, records, getResultsPerGFA, displayCurrency],
  );

  return createElement(
    ElementListMaxValuesContext.Provider,
    { value },
    children,
  );
};

/**
 * Max co2e and cost per GFA across all elements in the selected version,
 * for use as the bar chart scale in element lists.
 *
 * Reads from {@link ElementListMaxValuesProvider}; returns zeros when used
 * outside a provider.
 */
export const useElementListMaxValues = (): ElementListMaxValues =>
  useContext(ElementListMaxValuesContext) ?? EMPTY_ELEMENT_LIST_MAX_VALUES;

export type UseProposalsResultsMaxValueOptions = {
  /**
   * When true, the scope's carbon target ({@link useResolvedProjectTarget}) is
   * folded into the CO₂ scale max, so its line cannot fall outside the overview
   * chart. When false, max is only baseline and proposal scenario totals.
   * @default false
   */
  includeProjectTargetInCo2Scale?: boolean;
};

/**
 * Proposal overview CO₂ / cost vertical scale from lifecycle-filtered result records.
 * CO₂ optionally includes project target — see {@link UseProposalsResultsMaxValueOptions.includeProjectTargetInCo2Scale}.
 */
export const useProposalsResultsMaxValue = (
  options: UseProposalsResultsMaxValueOptions = {},
) => {
  const { includeProjectTargetInCo2Scale = false } = options;
  const target = useResolvedProjectTarget();
  const versionId = useSelectedVersionId();
  const proposalsResultRecords = useProposalsResultRecords();
  const proposals = useProposals();
  const getResultsPerGFA = useGetResultsPerGFA();

  const co2ePerGfaMax = useMemo(
    () =>
      computeProposalOverviewCo2PerGfaScaleMax(
        versionId,
        proposalsResultRecords as Record<string, ResultsRecord>,
        proposals,
        getResultsPerGFA,
        includeProjectTargetInCo2Scale
          ? { targetsKgCo2ePerM2: [target] }
          : undefined,
      ),
    [
      includeProjectTargetInCo2Scale,
      target,
      versionId,
      proposalsResultRecords,
      proposals,
      getResultsPerGFA,
    ],
  );

  const costPerGfaMax = useMemo(
    () =>
      computeProposalOverviewCostPerGfaScaleMax(
        versionId,
        proposalsResultRecords as Record<string, ResultsRecord>,
        proposals,
        getResultsPerGFA,
      ),
    [versionId, proposalsResultRecords, proposals, getResultsPerGFA],
  );

  return useObjectMemo({ co2ePerGfaMax, costPerGfaMax });
};

export const useProposalsBarChartValues = (
  id?: IProposal['id'],
  chartScaleOptions?: UseProposalsResultsMaxValueOptions,
) => {
  const proposalResults = useProposalResults(id);
  const proposalResultsPerGFA = useResultsPerGFA(proposalResults);

  const { co2ePerGfaMax, costPerGfaMax } =
    useProposalsResultsMaxValue(chartScaleOptions);

  const co2eChartData = useMemo(
    () => getCo2eChartDataFromResults(proposalResultsPerGFA),
    [proposalResultsPerGFA],
  );
  const costChartData = useMemo(
    () => getCostChartDataFromResults(proposalResultsPerGFA),
    [proposalResultsPerGFA],
  );

  return useObjectMemo({
    resultsPerGFA: proposalResultsPerGFA,
    /**
     * Bar chart data with CO2e per GFA for each LCA phase in proposal.
     */
    co2eChartData,
    /**
     * Bar chart data with cost per GFA for each LCA phase in proposal.
     */
    costChartData,
    /**
     * Total CO2e per GFA for proposal.
     */
    co2ePerGFA: getConversionFactorValue(proposalResultsPerGFA, 'co2e'),
    /**
     * Total cost per GFA for proposal.
     */
    costPerGFA: getConversionFactorValue(proposalResultsPerGFA, 'sek'),
    /**
     * CO₂ bar scale (kg CO₂e/m² GFA): from {@link useProposalsResultsMaxValue} with the same
     * `chartScaleOptions` (target included only when {@link UseProposalsResultsMaxValueOptions.includeProjectTargetInCo2Scale}).
     */
    co2ePerGfaMax,
    /**
     * Cost bar scale (SEK/m² GFA): maximum of baseline total and each proposal total.
     */
    costPerGfaMax,
  });
};
